import { AnchorHTMLAttributes, ReactNode } from "react";

import ReactMarkdown from "react-markdown";
import { Link } from "react-router";
import rehypeRaw from "rehype-raw";

import {
  getPlacementLinkFromUrl,
  isPlacementLink,
} from "@/components/helpers/url-utils";
import { AttachmentComponent } from "@/components/pieces/editor/tiptap/AttachmentNode";
import { LearnQuickViewLink } from "@/components/pieces/learn/LearnQuickViewLink";

export function CustomLink({
  href,
  children,
  ...props
}: AnchorHTMLAttributes<HTMLAnchorElement>) {
  // If this is an internal link, route to it using Link instead
  if (href && (isPlacementLink(href) || href.startsWith("/"))) {
    const barePath = getPlacementLinkFromUrl(href);

    if (barePath.startsWith("/learn/")) {
      return (
        <LearnQuickViewLink link={barePath}>{children}</LearnQuickViewLink>
      );
    } else {
      return <Link to={barePath}>{children}</Link>;
    }
  } else {
    return (
      <a href={href} target="_BLANK" rel="noopener noreferrer" {...props}>
        {children}
      </a>
    );
  }
}

export function SmartLinkMarkdown({
  children,
  ...props
}: { children: ReactNode } & Parameters<typeof ReactMarkdown>[0]) {
  return (
    <ReactMarkdown
      rehypePlugins={[rehypeRaw]}
      components={{
        a: CustomLink,
        div: ({ node, ...props }) => {
          if ("data-exec-id" in props) {
            return (
              <AttachmentComponent
                urlOrError={String(node?.properties.dataUrlOrError)}
                fileType={String(node?.properties.dataFileType)}
                fileSize={Number(node?.properties.dataFileSize)}
                error={String(node?.properties.dataExecId).startsWith("error")}
              >
                {props.children}
              </AttachmentComponent>
            );
          }
          return <div {...props} />;
        },
        ul: ({ ...props }) => {
          return (
            <ul {...props} className="list-disc pl-6 [&_ul]:list-[revert]" />
          );
        },
        ol: ({ ...props }) => {
          return <ol {...props} className="list-decimal pl-6" />;
        },
      }}
      {...props}
    >
      {children}
    </ReactMarkdown>
  );
}
